// src/components/WelcomeModal.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useTour } from '../context/TourContext';
import { useSettings } from '../context/SettingsContext';
import { createUpgradeSession } from '../services/stripe.service';
import { getTierById, getNextTier } from '../config/pricing';
import {
  X,
  Crown,
  Sparkles,
  Clock,
  Package,
  TrendingUp,
  Receipt,
  BarChart3,
  Loader2,
  Check,
  Play,
  ChevronRight
} from 'lucide-react';
import { toast } from './Toast';

const WelcomeModal = () => {
  const { currentUser } = useAuth();
  const { loading, subscriptionStatus, FREE_LIMITS } = useSubscription();
  const { restartTour, skipTour, onWelcomeModalClosed } = useTour();
  const { updateSettings, settings, isLoading: settingsLoading } = useSettings();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isPro = subscriptionStatus?.isPro;
  const currentTier = subscriptionStatus?.currentTier || 'free';
  const currentTierInfo = getTierById(currentTier);
  const nextTier = getNextTier(currentTier);

  // Check if this is a new user or if welcome was manually triggered
  useEffect(() => {
    // Wait for auth, subscription, and settings to fully load
    if (!currentUser || loading || settingsLoading) {
      return;
    }

    // Check if user has already completed the tour (synced from database)
    // This ensures the modal won't show on new devices if tour was already completed
    if (settings?.hasCompletedTour === true) {
      // Also set localStorage so we don't check again this session
      localStorage.setItem(`nexus_welcome_shown_${currentUser.uid}`, 'true');
      return;
    }

    // Safety net: if the account is older than 5 minutes, they've already been onboarded.
    // This handles cases where hasCompletedTour failed to save to the DB.
    if (currentUser.created_at) {
      const accountAge = Date.now() - new Date(currentUser.created_at).getTime();
      const FIVE_MINUTES = 5 * 60 * 1000;
      if (accountAge > FIVE_MINUTES) {
        localStorage.setItem(`nexus_welcome_shown_${currentUser.uid}`, 'true');
        // Fix the missing DB flag while we're at it
        updateSettings({ hasCompletedTour: true }).catch(() => {});
        return;
      }
    }

    // Check if we've already shown the welcome modal for this user (device-specific)
    const welcomeShown = localStorage.getItem(`nexus_welcome_shown_${currentUser.uid}`);

    // Check if this is a manual restart (localStorage was just cleared)
    const forceShow = sessionStorage.getItem('nexus_force_welcome');
    if (forceShow) {
      sessionStorage.removeItem('nexus_force_welcome');
      setTimeout(() => setIsOpen(true), 500);
      return;
    }

    // If already shown on this device, don't show again
    if (welcomeShown) return;

    // Claim the welcome modal for THIS tab by writing to localStorage immediately.
    // Other tabs listening for storage events will see this and won't show their own modal.
    localStorage.setItem(`nexus_welcome_shown_${currentUser.uid}`, 'true');

    setTimeout(() => setIsOpen(true), 500);
  }, [currentUser, loading, settingsLoading, settings?.hasCompletedTour]);

  // Listen for other tabs claiming the welcome modal — if another tab wrote the flag, close ours
  useEffect(() => {
    const handleStorage = (e) => {
      if (currentUser && e.key === `nexus_welcome_shown_${currentUser.uid}` && e.newValue === 'true') {
        setIsOpen(false);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [currentUser]);

  const handleClose = () => {
    if (currentUser) {
      localStorage.setItem(`nexus_welcome_shown_${currentUser.uid}`, 'true');
    }
    setIsOpen(false);
    // Notify TourContext that the WelcomeModal has been closed
    onWelcomeModalClosed();
  };

  const handleStartTour = () => {
    // Close modal visually without notifying TourContext yet —
    // restartTour() will handle the tour lifecycle directly
    if (currentUser) {
      localStorage.setItem(`nexus_welcome_shown_${currentUser.uid}`, 'true');
    }
    setIsOpen(false);
    // Small delay to let modal close animation complete, then start tour
    // skipWelcomeStep=true because user already saw the WelcomeModal
    setTimeout(() => {
      restartTour(true);
    }, 300);
  };

  const handleSkipTour = async () => {
    // Mark tour as completed so it doesn't auto-start after modal closes
    skipTour();
    // Save to settings to persist across sessions and devices
    // Retry once on failure to ensure it sticks
    const result = await updateSettings({ hasCompletedTour: true });
    if (result?.error) {
      // Retry after a short delay (settings row may not be ready yet)
      setTimeout(() => updateSettings({ hasCompletedTour: true }).catch(() => {}), 1000);
    }
    handleClose();
    if (isPro) {
      toast.success(`Welcome to Nexus ${currentTierInfo.name}! Enjoy your plan features.`);
    } else {
      toast.success('Welcome to Nexus! Your free account is ready.');
    }
  };

  const handleUpgradeToPro = async () => {
    if (!currentUser) return;

    setCheckoutLoading(true);
    try {
      const upgradeTo = nextTier || { id: 'pro' };
      const result = await createUpgradeSession(
        upgradeTo.id,
        'month'
      );

      if (result.error) {
        toast.error(result.error);
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  const features = [
      { icon: Package, text: 'Inventory tracking' },
      { icon: TrendingUp, text: 'Sales analytics' },
      { icon: Receipt, text: 'Expense logging' },
      { icon: BarChart3, text: 'Reports' },
    ];

    return (
      <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />

        <div className="relative w-full max-w-lg bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className={`relative px-8 pt-10 pb-6 text-white text-center ${
            isPro
              ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500'
              : 'bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500'
          }`}>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9zdmc+')] opacity-50" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-sm mb-4">
                {isPro ? <Crown className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                <span>{isPro ? `Nexus ${currentTierInfo.name} Member` : 'Welcome to Nexus!'}</span>
              </div>

              <h2 className="text-2xl font-bold mb-2">
                {isPro ? `Welcome to Nexus ${currentTierInfo.name}!` : 'Your account is ready!'}
              </h2>
              <p className={isPro ? 'text-amber-100' : 'text-blue-100'}>
                {isPro ? 'You have full access to your plan features' : 'Start tracking your inventory for free'}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            {/* Features grid */}
            <div className="mb-6">
              <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-3">
                Everything included:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <feature.icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Free/Pro info */}
            {isPro ? (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 mb-6">
                <Crown className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                <p className="text-sm text-green-800 dark:text-green-200">
                  <span className="font-semibold">{currentTierInfo.name} membership active.</span>
                  {' '}Enjoy your plan features.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 mb-6">
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-semibold">Free plan active.</span>
                  {' '}Up to {FREE_LIMITS?.MAX_INVENTORY_ITEMS || 200} items, {FREE_LIMITS?.MAX_SALES || 50} sales.
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              {/* Take the tour button */}
              <button
                onClick={handleStartTour}
                className={`w-full py-3 px-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-lg ${
                  isPro
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'
                }`}
              >
                <Play className="w-4 h-4" />
                Take a Quick Tour
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Skip tour */}
              <button
                onClick={handleSkipTour}
                className="w-full py-2.5 px-4 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 font-medium flex items-center justify-center gap-2 transition-colors text-sm"
              >
                Skip, I'll explore on my own
              </button>

              {/* Only show upgrade section for non-Pro users */}
              {!isPro && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-zinc-200 dark:border-zinc-700" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white dark:bg-zinc-900 text-zinc-500">or upgrade now</span>
                    </div>
                  </div>

                  <button
                    onClick={handleUpgradeToPro}
                    disabled={checkoutLoading}
                    className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-medium flex items-center justify-center gap-2 transition-all hover:shadow-lg disabled:opacity-50 text-sm"
                  >
                    {checkoutLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Crown className="w-4 h-4" />
                    )}
                    Upgrade to {nextTier ? nextTier.name : 'Pro'} - ${nextTier ? nextTier.monthlyPrice : 15}/month
                  </button>
                </>
              )}
            </div>

            {/* Pro benefits / Pro status */}
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-zinc-500">
              {isPro ? (
                <>
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    Unlimited storage
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    Priority support
                  </span>
                </>
              ) : (
                <>
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    Cancel anytime
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    Priority support
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
};

export default WelcomeModal;
